// One-shot bootstrap loader:
//   • Renders a "Disclaimer" modal on first launch (and any time admin bumps
//     disclaimer_version) — user must tap "Accept" to continue.
//   • Renders a "Push notification" popup when admin has one active.
//   • Merges server-managed wallpapers into ThemeContext.
// All three are additive. App works if /api/bootstrap is unreachable.
import { useEffect, useState } from "react";
import axios from "axios";
import { useTheme } from "../contexts/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NOTIF_ACK  = "nettv.notification_ack_version";
const DISC_ACK   = "nettv.disclaimer_ack_version";

export default function BootstrapGate() {
  const [notif, setNotif] = useState(null);
  const [disc,  setDisc]  = useState(null);
  const { wallpapers, addCustomWallpaper } = useTheme();

  useEffect(() => {
    const deviceKey = localStorage.getItem("deviceKey") || localStorage.getItem("device_key");
    let cancelled = false;
    (async () => {
      try {
        // Disclaimer can be shown even before the device is registered —
        // use the public /api/content endpoint when we don't have a key yet.
        const endpoint = deviceKey
          ? `${API}/bootstrap/${deviceKey}`
          : `${API}/content`;
        const { data } = await axios.get(endpoint, { timeout: 8000 });
        if (cancelled) return;

        // bootstrap wraps content under .content; /api/content returns flat
        const c = data?.content || data || {};

        // 1) Disclaimer gate — blocks the app until acknowledged
        if (c.disclaimer_active && c.disclaimer_text) {
          const acked = Number(localStorage.getItem(DISC_ACK) || 0);
          const cur   = Number(c.disclaimer_version || 1);
          if (cur > acked) {
            setDisc({ text: c.disclaimer_text, version: cur });
          }
        }

        // 2) Admin push notification (secondary, non-blocking)
        if (c.notification_active && c.notification_title) {
          const acked = Number(localStorage.getItem(NOTIF_ACK) || 0);
          const cur   = Number(c.notification_version || 0);
          if (cur > acked) {
            setNotif({
              title: c.notification_title,
              content: c.notification_content,
              version: cur,
            });
          }
        }

        // 3) Merge server wallpapers
        const serverWps = Array.isArray(data?.wallpapers) ? data.wallpapers : [];
        const existing = new Set(wallpapers.map((w) => w.url).filter(Boolean));
        for (const w of serverWps) {
          if (!w?.url || existing.has(w.url)) continue;
          addCustomWallpaper(BACKEND_URL + w.url, w.name || "Wallpaper");
        }
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("bootstrap failed:", e?.message || e);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Render disclaimer first (blocks everything). Then notification. ---

  if (disc) {
    const accept = () => {
      localStorage.setItem(DISC_ACK, String(disc.version));
      setDisc(null);
    };
    return (
      <div
        data-testid="disclaimer-gate"
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] grid place-items-center bg-black/85 backdrop-blur-md p-4"
      >
        <div className="max-w-lg w-full rounded-2xl border border-white/10 bg-[#0f1624] text-white shadow-2xl">
          <div
            className="p-5 text-sm text-white/90 leading-relaxed max-h-[65vh] overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: disc.text }}
          />
          <div className="p-4 border-t border-white/10 flex justify-end">
            <button
              data-testid="disclaimer-accept"
              onClick={accept}
              className="px-5 py-2 rounded-lg bg-cyan-500 text-black font-semibold hover:bg-cyan-400 transition-colors"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!notif) return null;
  const dismissNotif = () => {
    localStorage.setItem(NOTIF_ACK, String(notif.version));
    setNotif(null);
  };
  return (
    <div
      data-testid="bootstrap-notification"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] grid place-items-center bg-black/70 backdrop-blur-sm p-4"
    >
      <div className="max-w-md w-full rounded-2xl border border-white/10 bg-[#0f1624] text-white shadow-2xl">
        <div className="p-5 border-b border-white/10">
          <div className="text-lg font-semibold text-cyan-400">{notif.title}</div>
        </div>
        <div
          className="p-5 text-sm text-white/80 leading-relaxed max-h-[60vh] overflow-y-auto"
          dangerouslySetInnerHTML={{ __html: notif.content || "" }}
        />
        <div className="p-4 flex justify-end">
          <button
            data-testid="bootstrap-notification-dismiss"
            onClick={dismissNotif}
            className="px-4 py-2 rounded-lg bg-cyan-500 text-black font-medium hover:bg-cyan-400 transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
